/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import { Icon } from '../../../components';
import CounterControl from '../../../components/CounterControl';
import DropdownControl from '../../../components/DropdownControl';
import RadioControl from '../../../components/RadioControl';
import Styled from './DynamicFormSection.style';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-styled-flexboxgrid';
import { FormSectionConfig } from '../Washroom/washroomJson';

interface DynamicFormSectionProps {
  config: FormSectionConfig;
  onChange?: (value: any) => void;
  initialValue?: any;
}

const createEmptyGroup = (controls) => {
  return controls.reduce((group, control) => {
    group[control.key] = control.type === 'counter' ? (control.min ?? 0) : '';

    return group;
  }, {});
};

const DynamicFormSection: React.FC<DynamicFormSectionProps> = ({
  config,
  onChange,
  initialValue,
}) => {
  const { t } = useTranslation('common');
  console.log('SECTION', config.key, initialValue);
  const { key, title, icon } = config;

  const hasGroups = !!config.group && !!config.counter;
  const hasStandaloneControls = !!config.controls;
  const group = config.group;
  const counter = config.counter;
  const [sectionAnswers, setSectionAnswers] = React.useState(
    initialValue ?? {},
  );
  const hasInitialData = React.useMemo(() => {
    if (!initialValue) return false;

    if (typeof initialValue.count === 'number' && initialValue.count > 0) {
      return true;
    }

    return Object.keys(initialValue).some((key) => {
      const value = initialValue[key];

      if (Array.isArray(value)) return value.length > 0;

      if (value && typeof value === 'object')
        return Object.keys(value).length > 0;

      return value !== '' && value !== null && value !== undefined;
    });
  }, [initialValue]);
  const [isOpen, setIsOpen] = React.useState(hasInitialData);

  const [count, setCount] = React.useState(initialValue?.count ?? 0);

  const [copyFirstGroup, setCopyFirstGroup] = React.useState('no');

  const [groups, setGroups] = React.useState(() => {
    if (initialValue?.groups) {
      return Array.from(
        { length: counter?.max ?? initialValue.groups.length },
        (_, index) =>
          initialValue.groups[index] ?? createEmptyGroup(group!.controls),
      );
    }

    return hasGroups
      ? Array.from({ length: counter!.max }, () =>
          createEmptyGroup(group!.controls),
        )
      : [];
  });

  React.useEffect(() => {
    onChange?.({
      count,
      groups: groups.slice(0, count),
      ...sectionAnswers,
    });
  }, [count, groups, sectionAnswers, onChange]);
  React.useEffect(() => {
    if (!initialValue) return;

    setCount(initialValue.count ?? 0);

    setGroups((prev) => {
      if (!initialValue.groups) return prev;

      return Array.from(
        { length: counter?.max ?? initialValue.groups.length },
        (_, index) =>
          initialValue.groups[index] ?? createEmptyGroup(group!.controls),
      );
    });

    setSectionAnswers(initialValue);
  }, []);
  const hasInitialised = React.useRef(false);

  React.useEffect(() => {
    if (!initialValue || hasInitialised.current) return;

    hasInitialised.current = true;

    setCount(initialValue.count ?? 0);

    if (initialValue.groups) {
      setGroups(
        Array.from(
          { length: counter?.max ?? initialValue.groups.length },
          (_, index) =>
            initialValue.groups[index] ?? createEmptyGroup(group!.controls),
        ),
      );
    }

    setSectionAnswers(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    if (hasInitialData) {
      setIsOpen(true);
    }
  }, [hasInitialData]);
  const updateCounter = (_answerKey: string, value: number) => {
    setCount(value);
  };

  const updateGroup = (
    index: number,
    answerKey: string,
    value: string | number,
  ) => {
    setGroups((prev) => {
      const updated = prev.map((groupState, i) =>
        i === index
          ? {
              ...groupState,
              [answerKey]: value,
            }
          : groupState,
      );

      if (copyFirstGroup === 'yes' && index === 0) {
        const first = updated[0];

        return updated.map((groupState, i) =>
          i === 0
            ? groupState
            : {
                ...first,
              },
        );
      }

      return updated;
    });
  };
  const updateSectionAnswer = (key: string, value: string | number) => {
    setSectionAnswers((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  const handleCopyFirstGroup = (value) => {
    setCopyFirstGroup(value);

    setGroups((prev) => {
      if (value === 'yes') {
        const first = prev[0];

        return prev.map((groupState, index) =>
          index === 0
            ? groupState
            : {
                ...first,
              },
        );
      }

      return prev.map((groupState, index) =>
        index === 0 ? groupState : createEmptyGroup(config.group!.controls),
      );
    });
  };
  const arrowSvg = (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18 15L12 9L6 15"
        stroke="#21AAE8"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
  return (
    <Styled.Box>
      <Styled.BoxTitleContainer onClick={() => setIsOpen((prev) => !prev)}>
        <Icon icon={icon} size={80} />

        <Styled.BoxTitle>{t(title)}</Styled.BoxTitle>

        <Styled.Chevron $isOpen={isOpen}>{arrowSvg}</Styled.Chevron>
      </Styled.BoxTitleContainer>

      <Styled.AccordionContent $isOpen={isOpen}>
        <Styled.BoxContentContainer>
          <Row style={{ width: '100%' }}>
            {hasGroups && (
              <>
                <Col md={6} xs={12}>
                  <Styled.BoxContentQuestion>
                    <CounterControl
                      answerKey={key}
                      label="How many do you have?"
                      value={count}
                      defaultValue="0"
                      min={counter!.min}
                      max={counter!.max}
                      updateAnswerKey={updateCounter}
                    />
                  </Styled.BoxContentQuestion>
                </Col>

                <Col md={12}>
                  {groups.slice(0, count).map((groupState, index) => (
                    <Row key={index}>
                      <Col md={12}>
                        <h3>
                          {t(group!.titlePrefix)} {index + 1}
                        </h3>
                      </Col>

                      {group!.controls.map((control) => (
                        <React.Fragment key={control.key}>
                          {control.type === 'dropdown' && (
                            <Col md={6} xs={12}>
                              <DropdownControl
                                answerKey={control.key}
                                value={groupState[control.key]}
                                options={control.options.map((option) => ({
                                  ...option,
                                  label: t(option.label),
                                }))}
                                updateAnswerKey={(key, value) =>
                                  updateGroup(index, key, value)
                                }
                              />
                            </Col>
                          )}

                          {control.type === 'counter' && (
                            <Col md={6} xs={12}>
                              <CounterControl
                                answerKey={control.key}
                                label={control.label}
                                value={Number(groupState[control.key])}
                                defaultValue={String(control.min)}
                                min={control.min ?? 0}
                                max={control.max ?? 30}
                                colored
                                updateAnswerKey={(key, value) =>
                                  updateGroup(index, key, value)
                                }
                              />
                            </Col>
                          )}
                        </React.Fragment>
                      ))}

                      {index === 0 && group!.copyFirstGroup.enabled && (
                        <Col md={6}>
                          <RadioControl
                            answerKey="copyFirstGroup"
                            key="copyFirstGroup"
                            title="Same answers for the rest?"
                            value={copyFirstGroup}
                            style={{ background: '#F1B91A' }}
                            text=""
                            options={group!.copyFirstGroup.options.map(
                              (option) => ({
                                ...option,
                                text: t(option.text),
                              }),
                            )}
                            updateAnswerKey={(_, value) =>
                              handleCopyFirstGroup(value)
                            }
                          />
                        </Col>
                      )}
                    </Row>
                  ))}
                </Col>
              </>
            )}

            {hasStandaloneControls &&
              (config.controls ?? []).map((control) => (
                <React.Fragment key={control.key}>
                  {control.type === 'dropdown' && (
                    <Col md={6} xs={12}>
                      <DropdownControl
                        answerKey={control.key}
                        value={sectionAnswers[control.key]}
                        options={control.options ?? []}
                        updateAnswerKey={(key, value) => {
                          console.log('standalone update:', key, value);
                          updateSectionAnswer(key, value);
                        }}
                      />
                    </Col>
                  )}

                  {control.type === 'counter' && (
                    <Col md={6} xs={12}>
                      <CounterControl
                        answerKey={control.key}
                        label={control.label}
                        value={Number(
                          sectionAnswers[control.key] ?? control.min,
                        )}
                        defaultValue={String(control.min)}
                        min={control.min ?? 0}
                        max={control.max ?? 30}
                        colored
                        updateAnswerKey={(key, value) => {
                          console.log('standalone update:', key, value);
                          updateSectionAnswer(key, value);
                        }}
                      />
                    </Col>
                  )}

                  {control.type === 'radio' && (
                    <Col md={6} xs={12}>
                      <RadioControl
                        answerKey={control.key}
                        key={control.key}
                        title={t(control.label)}
                        value={sectionAnswers[control.key]}
                        text=""
                        options={(control.options ?? []).map((option) => ({
                          value: option.value,
                          text: option.label,
                        }))}
                        updateAnswerKey={(key, value) => {
                          console.log('standalone update:', key, value);
                          updateSectionAnswer(key, value);
                        }}
                      />
                    </Col>
                  )}
                </React.Fragment>
              ))}
          </Row>
        </Styled.BoxContentContainer>
      </Styled.AccordionContent>
    </Styled.Box>
  );
};

export default DynamicFormSection;
